import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/* =======================
   DATA MODELS
   ======================= */

export interface Session {
  id: number;
  session_id: string;
  header: string;
  created_at: string;
}

export interface Message {
  id: number;
  user_message: string;
  timestamp: string;
}

export interface Bookmark {
  id: string;
  bookmark_type: string;
  title: string;
  display_title: string;
  created_at: string;
  session: Session;
  message: Message;
}

/* =======================
   REDUX STATE SHAPE
   ======================= */

interface BookmarkState {
  loading: boolean;
  error: string | null;

  // Aggregated list shown in UI
  bookmarks: Bookmark[];

  // Pagination metadata from API
  count: number;
  next_book: string | null;
  previous: string | null;
}

/* =======================
   INITIAL STATE
   ======================= */

const initialState: BookmarkState = {
  loading: false,
  error: null,
  bookmarks: [],
  count: 0,
  next_book: null,
  previous: null,
};

/* =======================
   SLICE
   ======================= */

const getBookmarkSlice = createSlice({
  name: 'bookmark',
  initialState,
  reducers: {
    /**
     * Triggered by UI (BookmarkScreen)
     * Payload includes pagination & search info
     */
    fetchBookmarksRequest: (
      state,
      _action: PayloadAction<{
        offset: number;
        limit: number;
        search?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },

    /**
     * Called by Saga on successful API response
     * Handles BOTH:
     *  - fresh load (offset === 0)
     *  - pagination append (offset > 0)
     */
    fetchBookmarksSuccess: (
      state,
      action: PayloadAction<{
        count: number;
        next_book: string | null;
        previous: string | null;
        results: Bookmark[];
        offset: number;
      }>
    ) => {
      state.loading = false;

      const { results, count, next_book, previous, offset } = action.payload;

      // 🔁 Replace list on first page
      if (offset === 0) {
        state.bookmarks = results;
      }
      // ➕ Append list on pagination
      else {
        state.bookmarks.push(...results);
      }

      // Store pagination metadata
      state.count = count;
      state.next_book = next_book;
      state.previous = previous;
    },

    /**
     * API failure handler
     */
    fetchBookmarksFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    /**
     * Clears all bookmark data
     * Used on logout / reset
     */
    clearBookmarks: (state) => {
      state.bookmarks = [];
      state.count = 0;
      state.next_book = null;
      state.previous = null;
    },
  },
});

export const {
  fetchBookmarksRequest,
  fetchBookmarksSuccess,
  fetchBookmarksFailure,
  clearBookmarks,
} = getBookmarkSlice.actions;

export const getBookmarkReducer = getBookmarkSlice.reducer;
